import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

export const PageContainer = styled.div`
  background-color: #1a1a1a;
  color: #ffffff;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  flex: 1;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.4s ease-out;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.5s ease-out;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
`;

export const ExportButton = styled.button`
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 212, 255, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 212, 255, 0.4);
    background: linear-gradient(135deg, #00e0ff, #00a8dd);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 212, 255, 0.2);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

export const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
  color: #ffffff;
`;

export const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const SearchInput = styled.input`
  background-color: #2a2a2a;
  border: 1px solid #404040;
  border-radius: 8px;
  padding: 8px 35px 8px 12px;
  color: #ffffff;
  font-size: 14px;
  width: 200px;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: #888;
    transition: color 0.3s ease;
  }
  
  &:focus {
    outline: none;
    border-color: #1d388d;
    box-shadow: 0 0 0 3px rgba(29, 56, 141, 0.1);
    width: 250px;
  }

  &:focus::placeholder {
    color: #666;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  right: 10px;
  color: #888;
  pointer-events: none;
  transition: color 0.3s ease;

  ${SearchInput}:focus ~ & {
    color: #1d388d;
  }
`;

export const TableContainer = styled.div`
  background-color: #2a2a2a;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  animation: ${scaleIn} 0.5s ease-out;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHeader = styled.thead`
  background-color: #333;
`;

export const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #404040;
`;

export const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 500;
  font-size: 14px;
  color: #ccc;
  border-right: 1px solid #404040;
  transition: color 0.3s ease;
  
  &:last-child {
    border-right: none;
  }

  &:hover {
    color: #fff;
  }
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr`
  border-bottom: 1px solid #404040;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background-color: #333;
    transform: scale(1.002);
  }

  &:active {
    transform: scale(0.998);
  }
  
  &:last-child {
    border-bottom: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: linear-gradient(135deg, #00d4ff, #0099cc);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleY(1);
  }
`;

export const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #fff;
  border-right: 1px solid #404040;
  transition: padding 0.2s ease;
  
  &:last-child {
    border-right: none;
  }

  ${TableRow}:hover & {
    padding-left: 20px;
  }
`;

export const StatusBadge = styled.span<{ status: 'online' | 'offline' | 'warning' }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(0.9);
    }
  }
  
  ${props => {
    switch (props.status) {
      case 'online':
        return `
          background-color: #1d388d;
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(29, 56, 141, 0.3);
          
          &::before {
            background-color: #4ade80;
            box-shadow: 0 0 8px rgba(74, 222, 128, 0.6);
          }

          &:hover {
            background-color: #2d4da3;
            box-shadow: 0 4px 12px rgba(29, 56, 141, 0.5);
            transform: scale(1.05);
          }
        `;
      case 'warning':
        return `
          background-color: #ffc107;
          color: #000000;
          box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
          
          &::before {
            background-color: #ff9800;
            box-shadow: 0 0 8px rgba(255, 152, 0, 0.6);
          }

          &:hover {
            background-color: #ffcd38;
            box-shadow: 0 4px 12px rgba(255, 193, 7, 0.5);
            transform: scale(1.05);
          }
        `;
      case 'offline':
        return `
          background-color: #dc3545;
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
          
          &::before {
            background-color: #fff;
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
          }

          &:hover {
            background-color: #e4515f;
            box-shadow: 0 4px 12px rgba(220, 53, 69, 0.5);
            transform: scale(1.05);
          }
        `;
      default:
        return `
          background-color: #6c757d;
          color: #ffffff;
          
          &::before {
            background-color: #adb5bd;
          }
        `;
    }
  }}
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #ccc;
  font-size: 14px;
  animation: ${fadeIn} 0.6s ease-out;
`;

export const ShowingText = styled.div``;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const PaginationSelect = styled.select`
  background-color: #2a2a2a;
  border: 1px solid #404040;
  border-radius: 6px;
  padding: 6px 10px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #1d388d;
    box-shadow: 0 0 0 3px rgba(29, 56, 141, 0.1);
  }

  &:hover {
    border-color: #555;
    background-color: #333;
  }
  
  option {
    background-color: #2a2a2a;
    color: #fff;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  gap: 5px;
`;

export const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  background-color: ${props => props.active ? '#1d388d' : 'transparent'};
  border: 1px solid #404040;
  color: ${props => props.active ? '#fff' : '#ccc'};
  padding: 8px 12px;
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  min-width: 36px;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#2d4da3' : '#333'};
    border-color: ${props => props.active ? '#2d4da3' : '#555'};
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: #888;
  animation: ${fadeIn} 0.4s ease-out;

  &::after {
    content: '...';
    animation: ellipsis 1.5s infinite;
  }

  @keyframes ellipsis {
    0%, 20% { content: '.'; }
    40% { content: '..'; }
    60%, 100% { content: '...'; }
  }
`;

export const ErrorContainer = styled.div`
  background-color: #dc3545;
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
  border-left: 4px solid #c82333;
  animation: ${fadeIn} 0.4s ease-out;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #888;
  font-size: 16px;
  animation: ${fadeIn} 0.6s ease-out;
`;

export const EditableNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DeviceNameSpan = styled.span`
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #1d388d, #2d4da3);
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: #1d388d;
  }

  &:hover::after {
    width: 100%;
  }
`;

export const EditInput = styled.input`
  background-color: #1a1a1a;
  border: 2px solid #1d388d;
  border-radius: 6px;
  padding: 6px 10px;
  color: #fff;
  font-size: 14px;
  min-width: 150px;
  transition: all 0.3s ease;
  animation: ${scaleIn} 0.2s ease-out;
  
  &:focus {
    outline: none;
    border-color: #2d4da3;
    box-shadow: 0 0 0 3px rgba(29, 56, 141, 0.2);
  }
`;

export const EditButton = styled.button`
  background: none;
  border: none;
  color: #1d388d;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(29, 56, 141, 0.1);
    color: #2d4da3;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const ViewDataIconButton = styled.button`
  background: none;
  border: none;
  color: #1d388d;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(29, 56, 141, 0.1);
    color: #2d4da3;
    transform: scale(1.1) rotate(5deg);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const SaveButton = styled.button`
  background-color: #1d388d;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #2d4da3;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(29, 56, 141, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const CancelButton = styled.button`
  background-color: #dc3545;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #e4515f;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const DeviceTypeBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background-color: ${props => `${props.$color}15`};
  border: 1px solid ${props => `${props.$color}40`};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color};
  white-space: nowrap;
  transition: all 0.3s ease;
  
  svg {
    flex-shrink: 0;
    transition: transform 0.3s ease;
  }
  
  span {
    line-height: 1;
  }

  &:hover {
    background-color: ${props => `${props.$color}25`};
    border-color: ${props => `${props.$color}60`};
    transform: scale(1.05);
  }

  &:hover svg {
    transform: rotate(10deg) scale(1.1);
  }
`;
